import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarHeart, FileSignature, CalendarClock, Mail, Wallet, Copy, Check, CreditCard, ExternalLink, Loader2, ShieldCheck, Receipt } from "lucide-react";
import { toast } from "sonner";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { EMAIL, PAYPAL_ME_URL } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEP_ICONS = [CalendarHeart, FileSignature, CalendarClock, Mail, Wallet];
const OPTION_ICONS = { interac: Receipt, card: CreditCard, paypal: Wallet };

export default function Payments() {
  const { t, lang, to } = useLang();
  const p = t.payments;
  const [loading, setLoading] = useState(null);
  const [copied, setCopied] = useState(false);
  const [amounts, setAmounts] = useState({ card: "" });

  useEffect(() => {
    if (window.location.hash !== "#pay") return;
    const timer = setTimeout(() => {
      const el = document.getElementById("pay");
      if (!el) return;
      if (window.__lenis) {
        window.__lenis.scrollTo(el);
      } else {
        el.scrollIntoView();
      }
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(p.errorToast);
    }
  };

  const handlePay = async (method) => {
    const amount = parseFloat(amounts[method]);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error(p.amountError);
      return;
    }
    setLoading(method);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount,
          origin_url: window.location.origin + (lang === "es" ? "/es" : ""),
          lang,
        }),
      });
      if (!res.ok) throw new Error("checkout failed");
      const data = await res.json();
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(p.errorToast);
      setLoading(null);
    }
  };

  return (
    <>
      <section data-testid="payments-header" className="pt-32 sm:pt-40 pb-12 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <FadeUp className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-terracotta mb-4">{p.kicker}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] text-balance">
            {p.title}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-clay leading-relaxed">{p.intro}</p>
        </FadeUp>
      </section>

      <section data-testid="how-it-works-section" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-16">
        <FadeUp className="mb-10">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-taupe">{p.stepsTitle}</p>
        </FadeUp>
        <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {p.steps.map((step, i) => {
            const Icon = STEP_ICONS[i];
            return (
              <StaggerItem key={i}>
                <div data-testid={`how-it-works-step-${i + 1}`} className="h-full rounded-3xl bg-white border border-border p-6">
                  <span className="w-11 h-11 rounded-2xl bg-sage-soft flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-sage-dark" />
                  </span>
                  <p className="font-serif text-3xl text-terracotta/40 font-semibold">0{i + 1}</p>
                  <p className="mt-2.5 text-sm text-clay leading-relaxed">{step}</p>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>

      <section id="pay" data-testid="payment-options-section" className="bg-cream/70 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 sm:py-20">
          <FadeUp className="max-w-2xl mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">{p.optionsTitle}</h2>
            <p className="mt-4 text-base text-clay leading-relaxed">{p.optionsIntro}</p>
          </FadeUp>
          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {p.options.map((o) => {
              const Icon = OPTION_ICONS[o.id];
              const isInterac = o.id === "interac";
              const isPaypal = o.id === "paypal";
              return (
                <StaggerItem key={o.id}>
                  <div
                    data-testid={`pay-option-${o.id}`}
                    className={`h-full flex flex-col rounded-3xl bg-white p-8 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 ${
                      isInterac
                        ? "border-2 border-terracotta shadow-[0_24px_60px_-24px_rgba(200,109,81,0.4)]"
                        : "border border-border shadow-[0_16px_40px_-24px_rgba(44,37,32,0.25)]"
                    }`}
                  >
                    <span className="w-12 h-12 rounded-2xl bg-terracotta-soft flex items-center justify-center mb-6">
                      <Icon className="w-5 h-5 text-terracotta" />
                    </span>
                    <h3 className="font-serif text-xl sm:text-2xl font-semibold">{o.title}</h3>
                    <p className="mt-3 text-sm text-clay leading-relaxed flex-1">{o.desc}</p>
                    {isInterac ? (
                      <div className="mt-6">
                        <div className="rounded-2xl bg-espresso text-linen px-5 py-4 flex items-center justify-between gap-3">
                          <span className="text-sm sm:text-base font-semibold min-w-0" data-testid="interac-address">
                            {EMAIL.split("@")[0]}@<wbr />
                            {EMAIL.split("@")[1]}
                          </span>
                          <button
                            onClick={copyAddress}
                            data-testid="interac-copy-btn"
                            aria-label={p.copyAddress}
                            className="shrink-0 w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                          >
                            {copied ? <Check className="w-4 h-4 text-sage-soft" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                        <p className="mt-3 text-xs text-taupe">{copied ? p.copied : p.copyAddress}</p>
                      </div>
                    ) : isPaypal ? (
                      <a
                        href={PAYPAL_ME_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="pay-paypal-btn"
                        className="mt-6 self-start inline-flex items-center justify-center gap-2.5 rounded-full bg-terracotta text-linen px-6 py-3.5 text-sm font-semibold transition-[background-color,transform] duration-200 hover:bg-terracotta-dark [@media(hover:hover)]:hover:-translate-y-0.5"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {p.paypalCta}
                      </a>
                    ) : (
                      <div className="mt-6 space-y-3">
                        <label htmlFor={`amount-${o.id}`} className="block text-xs uppercase tracking-[0.15em] font-semibold text-taupe">
                          {p.amountLabel}
                        </label>
                        <div className="relative">
                          <input
                            id={`amount-${o.id}`}
                            data-testid={`pay-${o.id}-amount-input`}
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            placeholder={p.amountPlaceholder}
                            value={amounts[o.id]}
                            onChange={(e) => setAmounts((a) => ({ ...a, [o.id]: e.target.value }))}
                            className="w-full rounded-2xl border border-border bg-linen px-5 py-3.5 pr-14 text-base font-semibold text-espresso focus:outline-none focus:ring-2 focus:ring-terracotta/40 focus:border-terracotta"
                          />
                          <span className="absolute right-5 top-1/2 -translate-y-1/2 text-sm font-semibold text-taupe pointer-events-none">CAD</span>
                        </div>
                        <button
                          onClick={() => handlePay(o.id)}
                          disabled={loading !== null}
                          data-testid={`pay-${o.id}-btn`}
                          className="inline-flex items-center justify-center gap-2.5 rounded-full bg-terracotta text-linen px-6 py-3.5 text-sm font-semibold transition-[background-color,transform] duration-200 hover:bg-terracotta-dark [@media(hover:hover)]:hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                        >
                          {loading === o.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
                          {loading === o.id ? p.loading : p.payInvoice}
                        </button>
                      </div>
                    )}
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
          <FadeUp delay={0.1} className="mt-10 space-y-3">
            <p className="text-sm text-clay flex items-start gap-2.5" data-testid="invoice-note">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-sage-dark shrink-0" /> {p.invoiceNote}
            </p>
            <p className="text-sm text-clay flex items-start gap-2.5">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-sage-dark shrink-0" /> {p.secureNote}
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-14 text-center">
        <FadeUp>
          <p className="text-sm text-taupe">
            {p.ratherPre}{" "}
            <Link to={to("/booking")} data-testid="payments-book-first-link" className="inline-flex items-center gap-1.5 font-semibold text-terracotta hover:text-terracotta-dark">
              <CalendarHeart className="w-4 h-4" /> {p.ratherLink}
            </Link>{" "}
            {p.ratherPost}
          </p>
        </FadeUp>
      </section>
    </>
  );
}
